import axios from 'axios';
import toast from 'react-hot-toast';

// Smart API Configuration - Auto-detects environment
const getApiBaseUrl = () => {
  // Check if we have an environment variable first
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // Auto-detect based on current URL
  const currentUrl = window.location.hostname;
  
  // If running on localhost or 127.0.0.1, use local backend
  if (currentUrl === 'localhost' || currentUrl === '127.0.0.1') {
    return 'http://localhost:5001';
  }
  
  // If running on Cloudflare Pages (*.pages.dev), use live backend
  if (currentUrl.includes('.pages.dev') || currentUrl.includes('buildify')) {
    return 'https://buildify-backend.princerajputana5.workers.dev';
  }
  
  // Default fallback to live backend
  return 'https://buildify-backend.princerajputana5.workers.dev';
};

// Create axios instance
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Try to refresh token
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post(
            `${api.defaults.baseURL}/auth/refresh-token`,
            { refreshToken }
          );
          
          const { token, refreshToken: newRefreshToken } = response.data.data;
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }

    // Handle other errors
    const message = error.response?.data?.message || error.message || 'An error occurred';
    
    // Don't show toast for certain errors
    const silentErrors = [401, 403];
    if (!silentErrors.includes(error.response?.status)) {
      toast.error(message);
    }

    return Promise.reject(error);
  }
);

// Auth API functions
export const authAPI = {
  login: (credentials) => api.post('/api/auth/login', credentials),
  sellerLogin: (credentials) => api.post('/api/seller/login', credentials),
  register: (userData) => api.post('/api/auth/register', userData),
  getProfile: () => api.get('/api/auth/profile'),
  updateProfile: (data) => api.put('/api/auth/profile', data),
  changePassword: (data) => api.put('/api/auth/change-password', data),
  logout: () => api.post('/api/auth/logout'),
  uploadAvatar: (formData) => api.post('/api/auth/upload-avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Seller OTP endpoints
  sendSellerOTP: (phoneNumber) => api.post('/api/auth/send-seller-otp', { phoneNumber }),
  verifySellerOTP: (phoneNumber, otp) => api.post('/api/auth/verify-seller-otp', { phoneNumber, otp }),
};

// Admin API functions
export const adminAPI = {
  getDashboardStats: () => api.get('/api/admin/dashboard-stats'),
  getUsers: (params) => api.get('/api/admin/users', { params }),
  updateUserStatus: (userId, status) => api.put(`/api/admin/users/${userId}/status`, { status }),
  approveSeller: (sellerId) => api.put(`/api/admin/sellers/${sellerId}/approve`),
  rejectSeller: (sellerId, reason) => api.put(`/api/admin/sellers/${sellerId}/reject`, { reason }),
  getProducts: (params) => api.get('/api/admin/products', { params }),
  approveProduct: (productId) => api.put(`/api/admin/products/${productId}/approve`),
  rejectProduct: (productId, reason) => api.put(`/api/admin/products/${productId}/reject`, { reason }),
  getOrders: (params) => api.get('/api/admin/orders', { params }),
  updateOrderStatus: (orderId, status) => api.put(`/api/admin/orders/${orderId}/status`, { status }),
  getCategories: () => api.get('/api/admin/categories'),
  createCategory: (data) => api.post('/api/admin/categories', data),
  updateCategory: (id, data) => api.put(`/api/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/api/admin/categories/${id}`),
  getStats: () => api.get('/api/admin/stats'),
  getSellers: (sellerId) => api.get(`/api/admin/sellers/${sellerId}`),
};

// Seller API functions
export const sellerAPI = {
  // Dashboard and stats
  getDashboardStats: () => api.get('/api/seller/dashboard-stats'),
  getStats: () => api.get('/api/seller/stats'),
  
  // Profile management
  getProfile: () => api.get('/api/seller/profile'),
  updateProfile: (data) => api.put('/api/seller/profile', data),
  
  // Registration flow
  registerStep1: (data) => api.post('/api/seller/register/step1', data),
  registerStep2: (data) => api.post('/api/seller/register/step2', data),
  registerStep3: (data) => api.post('/api/seller/register/step3', data),
  registerStep4: (data) => api.post('/api/seller/register/step4', data),
  completeRegistration: () => api.post('/api/seller/register/complete'),
  saveProgress: (data) => api.post('/api/seller/save-progress', data),
  getRegistrationStatus: () => api.get('/api/seller/registration-status'),
  
  // Product management
  getProducts: (params) => api.get('/api/seller/products', { params }),
  createProduct: (data) => api.post('/api/seller/products', data),
  updateProduct: (id, data) => api.put(`/api/seller/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/api/seller/products/${id}`),
  uploadProductImages: (productId, formData) => api.post(`/api/seller/products/${productId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Order management
  getOrders: (params) => api.get('/api/seller/orders', { params }),
  updateOrderItemStatus: (orderId, itemId, status) => api.put(`/api/seller/orders/${orderId}/items/${itemId}/status`, { status }),
  addTrackingNumber: (orderId, itemId, trackingNumber) => api.put(`/api/seller/orders/${orderId}/items/${itemId}/tracking`, { trackingNumber }),
  
  // Categories
  getCategories: () => api.get('/api/seller/categories'),
  getCategoryFields: (categoryId) => api.get(`/api/seller/categories/${categoryId}/fields`),
};

// Customer API functions (for reference)
export const customerAPI = {
  getProducts: (params) => api.get('/api/customer/products', { params }),
  getProduct: (id) => api.get(`/api/customer/products/${id}`),
  getCategories: () => api.get('/api/customer/categories'),
  addToCart: (data) => api.post('/api/customer/cart', data),
  getCart: () => api.get('/api/customer/cart'),
  updateCartItem: (itemId, quantity) => api.put(`/api/customer/cart/${itemId}`, { quantity }),
  removeFromCart: (itemId) => api.delete(`/api/customer/cart/${itemId}`),
  createOrder: (data) => api.post('/api/customer/orders', data),
  getOrders: (params) => api.get('/api/customer/orders', { params }),
  getOrder: (id) => api.get(`/api/customer/orders/${id}`),
  cancelOrder: (id, reason) => api.put(`/api/customer/orders/${id}/cancel`, { reason }),
};

// Utility functions
export const handleApiError = (error) => {
  if (error.response?.data?.errors) {
    // Validation errors
    return error.response.data.errors.map(err => err.message).join(', ');
  }
  return error.response?.data?.message || error.message || 'An error occurred';
};

export const createFormData = (data) => {
  const formData = new FormData();
  
  Object.keys(data).forEach(key => {
    const value = data[key];
    
    if (value instanceof FileList) {
      Array.from(value).forEach(file => {
        formData.append(key, file);
      });
    } else if (Array.isArray(value)) {
      value.forEach(item => {
        formData.append(key, item);
      });
    } else if (typeof value === 'object' && value !== null) {
      formData.append(key, JSON.stringify(value));
    } else if (value !== null && value !== undefined) {
      formData.append(key, value);
    }
  });
  
  return formData;
};

export default api;
